/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBrand } from '@vaas/react-ui-kit';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon } from '@/components/Icon';
import { useHasWorkspace } from '@/auth/useAccess';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/uiStore';
import { hasPageTour } from '@/features/tour/tourSteps';
import { useThemeToggle } from './useThemeToggle';
import { loginRequest } from '@/auth/msalConfig';
import { Menu } from './Menu';
import { LanguageMenu } from './LanguageMenu';
import { PlantMenu } from './PlantMenu';
import { RAIL_ID } from './Rail';
import { SettingsModal } from './SettingsModal';
import { ReleaseNotesModal } from './ReleaseNotesModal';
import { AboutModal } from './AboutModal';
import { ProfileModal } from './ProfileModal';
import { VehiclePicker } from './VehiclePicker';

/** Help links. */
const USER_GUIDE_URL = '/user-guide/ewd-user-guide-v03.pdf';
const USER_GUIDE_VERSION = 'v03';
const SLACK_CHANNEL_URL = 'https://gm.enterprise.slack.com/archives/C093EFDL2LW';

type OpenMenu = 'help' | 'account' | 'language' | 'plant' | 'more' | null;

/** One compact "Label: value" chip in the top-bar workspace vehicle strip. */
function TbVehField({
  label,
  value,
  kind,
}: {
  label: string;
  value?: string;
  /** `vin` stays down to 600px, `extra` only shows from 1280px, the rest from 900px. */
  kind?: 'vin' | 'extra';
}) {
  if (!value) return null;
  return (
    <span className={cn('tb-veh-field', kind && `is-${kind}`)}>
      <span className="tb-veh-label">{label}:</span> <span className="tb-veh-val">{value}</span>
    </span>
  );
}

/**
 * Top application bar: brand, vehicle context, plant, theme, language, settings, help, account.
 *
 * <p>Below 1280px with a vehicle in the header, and below 600px everywhere, theme, language and
 * settings (and, on phones, the plant) fold into "More"; which of them shows is the stylesheet's
 * call, so the bar never re-renders on resize. Below 600px a menu button opens the rail drawer.
 */
export function Topbar({
  navOpen = false,
  onToggleNav,
}: {
  navOpen?: boolean;
  onToggleNav?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { resolved, toggle: toggleTheme } = useThemeToggle();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mode, isAuthenticated, name, initials, account, signIn, signOut } = useAuth();
  const showPicker = pathname.startsWith('/schematic-explorer') || pathname.startsWith('/mecl');
  // A plant is a manufacturing idea: it scopes repairs, scans and the EOLT line. Someone who only
  // holds Engineering has nothing to pick and no workflow that reads the choice, so the switcher
  // is theirs never to see rather than to see and find inert. False while grants resolve, so it
  // appears a beat late instead of flashing at someone who turns out not to hold the workspace.
  const picksAPlant = useHasWorkspace('MANUFACTURING');
  const plant = useUiStore((s) => s.plant);
  const workspaceVehicle = useUiStore((s) => s.workspaceVehicle);
  const openFeedback = useUiStore((s) => s.openFeedback);
  const openResources = useUiStore((s) => s.openResources);
  const startTour = useUiStore((s) => s.startTour);
  const releaseNotesOpen = useUiStore((s) => s.releaseNotesOpen);
  const openReleaseNotes = useUiStore((s) => s.openReleaseNotes);
  const closeReleaseNotes = useUiStore((s) => s.closeReleaseNotes);
  const aboutOpen = useUiStore((s) => s.aboutOpen);
  const openAbout = useUiStore((s) => s.openAbout);
  const closeAbout = useUiStore((s) => s.closeAbout);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // Single open-menu value: only one Topbar dropdown at a time.
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const setMenu = (menu: OpenMenu) => (open: boolean) => setOpenMenu(open ? menu : null);
  const close = () => setOpenMenu(null);
  // Plant and language open from "More" once their own triggers fold away; Escape returns there.
  const moreRef = useRef<HTMLButtonElement>(null);
  const focusMore = useCallback(() => moreRef.current, []);
  const hasVehicle = showPicker || Boolean(workspaceVehicle);
  const themeLabel = resolved === 'dark' ? t('settings.themeDark') : t('settings.themeLight');
  // Help > Quick tour starts the tour of the page on screen, so it is offered only where one exists.
  const tourHere = openMenu === 'help' && hasPageTour(pathname);

  return (
    <header className={cn('topbar', hasVehicle && 'has-vehicle')} id="app-header">
      <button
        type="button"
        className="tb-burger"
        onClick={onToggleNav}
        aria-label={t('nav.menu')}
        title={t('nav.menu')}
        aria-expanded={navOpen}
        aria-controls={RAIL_ID}
      >
        <Icon name="menu" size={22} />
      </button>

      <AppBrand
        className="tb-brand"
        title={t('topbar.brandTitle')}
        homeLabel={t('nav.home')}
        onNavigate={(href) => navigate(href)}
      />

      {workspaceVehicle && (
        <div className="tb-vehicle" role="group" aria-label={t('mfg.online.vehicle.title')}>
          <TbVehField label={t('mfg.online.vehicle.vin')} value={workspaceVehicle.vin} kind="vin" />
          <TbVehField
            label={t('mfg.online.vehicle.modelYear')}
            value={workspaceVehicle.modelYear}
            kind="extra"
          />
          <TbVehField
            label={t('mfg.online.vehicle.make')}
            value={workspaceVehicle.make}
            kind="extra"
          />
          <TbVehField label={t('mfg.online.vehicle.model')} value={workspaceVehicle.model} />
          <TbVehField
            label={t('mfg.online.vehicle.pvi')}
            value={workspaceVehicle.pvi}
            kind="extra"
          />
          <TbVehField
            label={t('mfg.online.vehicle.csn')}
            value={workspaceVehicle.csn}
            kind="extra"
          />
        </div>
      )}

      <div className="tb-sp" />
      {showPicker && <VehiclePicker />}
      <div className="tb-sp" />

      {picksAPlant && (
        <PlantMenu
          open={openMenu === 'plant'}
          onOpenChange={setMenu('plant')}
          returnFocus={focusMore}
        />
      )}

      <div className="tb-secondary">
        <button
          type="button"
          className="tb-ic"
          title={t('topbar.toggleTheme')}
          aria-label={t('topbar.toggleTheme')}
          data-tour="theme"
          onClick={toggleTheme}
        >
          {/* The theme that is on, like the kit ThemeToggle: sun in light, moon in dark. */}
          <Icon name={resolved === 'dark' ? 'moon' : 'sun'} size={20} />
        </button>

        <LanguageMenu
          open={openMenu === 'language'}
          onOpenChange={setMenu('language')}
          returnFocus={focusMore}
        />

        <button
          type="button"
          className="tb-ic"
          title={t('topbar.settings')}
          aria-label={t('topbar.settings')}
          data-tour="settings"
          onClick={() => setSettingsOpen(true)}
        >
          <Icon name="settings" size={20} />
        </button>
      </div>

      <Menu
        open={openMenu === 'more'}
        onOpenChange={setMenu('more')}
        className="tb-more-menu"
        label={t('topbar.more')}
        trigger={({ open, toggle }) => (
          <button
            ref={moreRef}
            type="button"
            className="tb-ic"
            title={t('topbar.more')}
            aria-label={t('topbar.more')}
            data-tour="more"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
          >
            <Icon name="more_vert" size={20} />
          </button>
        )}
      >
        <div className="menu-items">
          {/* Stays open: the value beside it changes, which is the confirmation. */}
          <button type="button" className="menu-item" onClick={toggleTheme}>
            <Icon name={resolved === 'dark' ? 'moon' : 'sun'} size={18} />
            {t('topbar.toggleTheme')}
            <span className="menu-item-meta">{themeLabel}</span>
          </button>
          {picksAPlant && (
            <button
              type="button"
              className="menu-item tb-more-only"
              onClick={() => setOpenMenu('plant')}
            >
              <Icon name="factory" size={18} />
              {t('topbar.plant.title')}
              <span className="menu-item-meta">{plant.plantCode || t('topbar.plant.none')}</span>
            </button>
          )}
          <button type="button" className="menu-item" onClick={() => setOpenMenu('language')}>
            <Icon name="language" size={18} />
            {t('language.title')}
            <span className="menu-item-meta">{i18n.language.toUpperCase()}</span>
          </button>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              setSettingsOpen(true);
            }}
          >
            <Icon name="settings" size={18} />
            {t('topbar.settings')}
          </button>
        </div>
      </Menu>

      <Menu
        open={openMenu === 'help'}
        onOpenChange={setMenu('help')}
        label={t('topbar.help')}
        trigger={({ open, toggle }) => (
          <button
            type="button"
            className="tb-ic"
            title={t('topbar.help')}
            aria-label={t('topbar.help')}
            data-tour="help"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
          >
            <Icon name="help" size={20} />
          </button>
        )}
      >
        <div className="menu-head">
          <div>
            <div className="menu-name">{t('topbar.help')}</div>
            <div className="menu-sub">{t('topbar.helpSub')}</div>
          </div>
        </div>
        <div className="menu-items">
          {tourHere && (
            <button
              type="button"
              className="menu-item"
              onClick={() => {
                close();
                startTour();
              }}
            >
              <Icon name="explore" size={18} /> {t('topbar.quickTour')}
            </button>
          )}
          <a
            className="menu-item"
            href={USER_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
            download={`ewd-user-guide-${USER_GUIDE_VERSION}.pdf`}
            onClick={close}
          >
            <Icon name="menu_book" size={18} />{' '}
            {t('topbar.userGuide', { version: USER_GUIDE_VERSION })}
          </a>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              openResources();
            }}
          >
            <Icon name="open_in_new" size={18} /> {t('topbar.resourceLinks')}
          </button>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              openReleaseNotes();
            }}
          >
            <Icon name="auto_awesome" size={18} /> {t('topbar.whatsNew')}
          </button>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              openFeedback();
            }}
          >
            <Icon name="chat" size={18} /> {t('topbar.sendFeedback')}
          </button>
          <a
            className="menu-item"
            href={SLACK_CHANNEL_URL}
            target="_blank"
            rel="noreferrer"
            onClick={close}
          >
            <Icon name="open_in_new" size={18} /> {t('topbar.discussion')}
          </a>
        </div>
      </Menu>

      <Menu
        open={openMenu === 'account'}
        onOpenChange={setMenu('account')}
        label={t('topbar.account')}
        trigger={({ open, toggle }) => (
          <button
            type="button"
            className="av"
            title={t('topbar.account')}
            aria-label={t('topbar.account')}
            data-tour="account"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
          >
            {initials || 'JT'}
          </button>
        )}
      >
        <div className="menu-head">
          <span className="menu-av" aria-hidden="true">
            {initials || 'JT'}
          </span>
          <div>
            <div className="menu-name">{name ?? t('topbar.developer')}</div>
            {account?.username && <div className="menu-sub">{account.username}</div>}
            <div className="menu-role">
              {account?.gmin
                ? t('topbar.gmin', { gmin: account.gmin })
                : mode === 'mock'
                  ? t('topbar.devAuth')
                  : t('topbar.entra')}
            </div>
          </div>
        </div>
        <div className="menu-items">
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              setProfileOpen(true);
            }}
          >
            <Icon name="person" size={18} /> {t('topbar.viewProfile')}
          </button>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              openAbout();
            }}
          >
            <Icon name="info" size={18} /> {t('topbar.about')}
          </button>
          {isAuthenticated ? (
            <button
              type="button"
              className="menu-item neg"
              onClick={() => {
                close();
                signOut();
              }}
            >
              <Icon name="logout" size={18} /> {t('topbar.signOut')}
            </button>
          ) : (
            <button
              type="button"
              className="menu-item"
              onClick={() => {
                close();
                signIn(loginRequest);
              }}
            >
              <Icon name="person" size={18} /> {t('topbar.signIn')}
            </button>
          )}
        </div>
      </Menu>

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
      {releaseNotesOpen && <ReleaseNotesModal onClose={closeReleaseNotes} />}
      {aboutOpen && <AboutModal onClose={closeAbout} />}
    </header>
  );
}
